// src/components/common/BottomSheetSelect.jsx
/**
 * BottomSheetSelect — single-select dropdown that adapts per platform.
 *
 * Mobile  : glass pill trigger → SwipeableDrawer bottom sheet with radio-style list
 * Desktop : standard MUI Select — 🔴 mit SCHWEBENDEM Etikett (`InputLabel
 *           shrink` + `notched`), auch bei leerem Wert. Hausform
 *           `VendorDetails.jsx:1072-1095`; entschieden mit `362ac27f43` fuer
 *           `BottomSheetMultiSelect`, hier am 2026-10-08 nachgezogen, weil
 *           `Case type` (/rma) und `Type`/`Channel`/`Tax case` (/invoicing)
 *           LEER stehen und ihr Etikett sonst im Feld liegt. Probe:
 *           `BottomSheetSelect.etikett.test.jsx`.
 *
 * 🔴 Von der Hausform uebernommen ist NUR die Etikettlage — ihr PLATZHALTER
 *    wird BEWUSST NICHT uebernommen (R16 W-N1, 2026-10-08). Beide zitierten
 *    Hausformen zeigen im leeren Fall einen Text (`UnifiedCreateWizard`
 *    „Select category", `VendorDetails` „No preference"); dieser Baustein zeigt
 *    NICHTS. Die Gruende: der Ausloeser zeigt auf dem Telefon ebenfalls nur das
 *    Etikett (`triggerLabel` unten), die Geschwister-Selects derselben
 *    Filterzeile zeigen im leeren Fall ebenso nichts, und leer heisst hier
 *    „kein Filter" — benannt wird er vom schwebenden Etikett, nicht von einem
 *    zweiten Wort im Feld. Wer die Zitierung als Uebernahme liest, liest sie
 *    falsch.
 *
 * 🔴 `desktopForm="pill"` (UI2 Teil 10, G0.4 / BS7) — der Facetten-Ausloeser.
 *    Er ersetzt das Desktop-FELD durch die Pille „Name ⌄" / „Name: Wert ⌄ ✕"
 *    und oeffnet statt der Klappliste eine **nicht-modale Flaeche**
 *    (`Popper` + `Paper`, KEIN MUI-`Popover`) mit einer `listbox`.
 *    🔴 **Rein additiv:** die Vorgabe ist `'field'`, also Zeichen fuer Zeichen
 *    die heutige Form — kein bestehender Aufrufer uebergibt das Prop.
 *    🔴 **Der Zweig ist DESKTOP-only** (Konzept §12 BS7 nennt ihn den
 *    „Desktop-Zweig"; „mobil unverändert Blatt"). Auf dem Telefon bleibt der
 *    Ausloeser Zeichen fuer Zeichen der heutige; seine `aria-haspopup`-Zusage
 *    ist dort **`"dialog"`** — die Flaeche IST das Blatt, und das traegt
 *    `role="dialog"`. Die Pille sagt `"listbox"` an. Die beiden Breiten sagen
 *    also nicht dasselbe an, sondern jede die Wahrheit ueber ihre eigene
 *    Flaeche (R16-W1 / R15-W4, 2026-10-12).
 *
 * 🔴 **Fokus (R16-Blocker 1 / R15-Bedingung 4, 2026-10-12).** Die Flaeche ist
 *    eine MUI-`MenuList` (`role="listbox"`), geoeffnet per `autoFocusItem`: der
 *    Fokus geht beim Oeffnen auf die **gewaehlte** Option, sonst auf die erste.
 *    Die Pfeiltasten/`Home`/`End`/Typeahead liefert `MenuList` — sie werden
 *    NICHT nachgebaut. `Tab` schliesst die Flaeche und gibt den Fokus an den
 *    Ausloeser zurueck (MUI-`Menu`-Verhalten), `Esc` ebenso.
 *    🔴 Der Wechsel `Popover` → `Popper` hatte den Fokusfang des `Modal`-
 *    Mantels mitgenommen und den Fokuseingang ersatzlos gestrichen; `Popper`
 *    bringt weder Fokus- noch Schliessverhalten mit. Beides ist hier selbst
 *    gebaut bzw. ueber `MenuList` geholt.
 *    🔴 **Kein `aria-modal`:** die Flaeche ist eine `listbox`, kein `dialog` —
 *    `aria-modal` gilt nur fuer `dialog`/`alertdialog`.
 *
 * 🔴 **Schliessen (R16-Blocker 1 / R15-Bedingung 3).** `ClickAwayListener` um
 *    das `Paper` (Hausform G0.5 `FilterSheet.jsx`; Vorbild
 *    `rma/components/ShipmentStatusPopover.jsx:92-96`): ein Aussenklick
 *    schliesst, der Ausloeser selbst ist ausgenommen, sonst schloesse und
 *    oeffnete derselbe Klick. Der Ausloeser **schaltet um** (Hausform
 *    MUI-`Select`): ein Klick auf den offenen Ausloeser schliesst.
 *    🔴 Kein `disablePortal`: die Leiste traegt die Ueberlaufmaske aus G0.1,
 *    eine ungeportete Flaeche wuerde daran abgeschnitten.
 *
 * 🔴 **Die Auswahl gibt den Fokus zurueck (R16-Blocker 1, Wiedervorlage 3,
 *    2026-10-12).** Der Klick auf eine Option ruft `onChange` und schliesst —
 *    damit baut der `Popper` seinen Inhalt ab, die fokussierte
 *    `role="option"` verschwindet, und der Browser setzt den Fokus auf
 *    `document.body` (WCAG 2.4.3). `anker.current?.focus()` laeuft deshalb
 *    SYNCHRON VOR `schliesse()` — dieselbe Form wie beim ✕. Der Weg traegt
 *    Klick UND Tastatur: `MenuItem` ist kein natives `<button>`, `ButtonBase`
 *    loest `onClick` fuer Enter und Leertaste aus. Esc, `Tab` und ✕ gaben den
 *    Fokus schon vorher zurueck; der Auswahlpfad war der vierte und einzige
 *    ohne Rueckfuehrung.
 *
 * 🔴 **Der Grund der leeren Liste steht auch maschinell und ist angesagt
 *    (R16-W1, Wiedervorlage 3).** Die Zeile traegt `data-reason` in den
 *    Woertern der Hausform `EmptyState` (`ready` → `no-data`; `loading` kein
 *    Wort) und eine `id`, auf die die `listbox` per `aria-describedby` zeigt:
 *    die leere Liste ist nicht fokussierbar, ihr Grund waere sonst stumm. Vier
 *    Gruende, vier Saetze — `unknown` hat einen EIGENEN („Options not loaded
 *    yet"), keinen Rueckfall auf `ready` (Konzept §8.1 / N8.3). Die Tabelle
 *    liegt in `leerText.js` und wird von BEIDEN Bausteinen gelesen (R15-W5).
 *
 * 🔴 **`disabled`: die Rolle bleibt, die Zusagen fallen (R16-W3, Wiedervorlage
 *    3).** `role="button"` steht auch bei `disabled` (MUI-Konvention fuer ein
 *    nicht-natives Bedienelement, `tabIndex={-1}`) — ohne sie haette
 *    `aria-disabled` keinen Traeger: ARIA 1.2 §6.5 kuendigt die globale
 *    Verwendung ab, getragen wird es von `button` (Used in Roles).
 *    `aria-haspopup`/`aria-expanded` haengen am `disabled`-Waechter: eine
 *    abgeschaltete Pille oeffnet nichts. Gilt in beiden Zweigen (Pille/Mobil).
 *
 * 🔴 Die Pille traegt `FACET_PILL_SX`, nicht `GLASS_PILL_SX`: die Glaspille ist
 *    32 px hoch, ein `size="small"`-Feld derselben Zeile 40 px — eine Facette
 *    neben einem Suchfeld stuende 8 px zu tief (N2.4 „eine Hoehe je Zeile",
 *    Plan §G0.4 F.4).
 *
 * Props:
 *   label      : string                      — label shown in trigger pill + sheet header
 *   value      : string                      — controlled selected value
 *   onChange   : (value: string) => void
 *   options    : [{ value: string, label: string, count?: number }]
 *                `count` ist additiv (UI2 Teil 10, G11a): steht er da, zeigt die
 *                LISTE „label (count)"; der Ausloeser zeigt weiter nur `label`.
 *                Ohne `count` ist die Ausgabe zeichengleich zur heutigen.
 *   clearable  : bool                        — if true: the "All" option in the
 *                                              mobile sheet and the ✕ in the `pill`
 *                                              trigger (the pill list has NO "All")
 *   disabled   : bool
 *   sx         : object                      — extra sx on the trigger (both variants)
 *   desktopForm: 'field' | 'pill'            — Vorgabe `'field'` = heutiges MUI-Select.
 *                                              `'pill'` = Facetten-Pille (G0.4, opt-in)
 *   optionsStatus: 'ready' | 'loading' | 'error' | 'unknown' — warum die
 *                                              Optionsliste leer ist; nur die
 *                                              PILLE liest es (Vorgabe `'ready'`).
 *                                              Ein Wert ausserhalb der Menge
 *                                              WIRFT (Tabelle in `leerText.js`)
 *
 * ⚠️ **Kein `summarize` hier, und das ist Absicht.** Der Plan nennt es in der
 *    gemeinsamen Zeile beider Bausteine; „3 selected" setzt aber drei WERTE
 *    voraus, und dieses Feld fuehrt genau einen. Ein Prop, das nichts tut, ist
 *    eine zweite Wahrheit ueber dieselbe Flaeche — es steht deshalb nur in
 *    `BottomSheetMultiSelect`, wo es wirkt.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  Box,
  Typography,
  SwipeableDrawer,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import Fachbegriff from './Fachbegriff';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  GLASS_PILL_SX,
  FACET_PILL_SX,
  FACET_PILL_MAX_WIDTH_PX,
  BOTTOM_SHEET_TRANSITION,
  LISTBOX_MAX_HEIGHT_PX,
  TOUCH_TARGET_MIN,
  FS,
  SP,
} from '../../theme/tokens';
// 🔴 R16-C1 (Wiedervorlage 3, 2026-10-12): die Gruende-Tabelle liegt seit
// dieser Runde in `leerText.js` — EINE Wahrheit fuer beide Bausteine (R15-W5).
// Sie stand vorher wortgleich hier UND in `BottomSheetMultiSelect`.
import { leerTextFuer, grundImDom } from './leerText';

/**
 * Optionstext fuer die LISTE — der Zaehler steht in der Zeile, nicht im
 * Ausloeser (UI2 Teil 10, G11a / §2.3: „Facetten-Zahl wandert aus dem Trigger
 * in die Optionsliste"). Ohne `count` ist die Ausgabe Zeichen fuer Zeichen die
 * heutige. Der Ausloeser liest weiterhin `opt.label`.
 */
const optionText = (opt) => (typeof opt.count === 'number' ? `${opt.label} (${opt.count})` : opt.label);

export default function BottomSheetSelect({
  label,
  value,
  onChange,
  options = [],
  clearable = false,
  disabled = false,
  sx = {},
  // Welle 3 PR 3 (§3): der deutsche Fachbegriff als `title` — die Beschriftung ist
  // englisch, der Rueckverweis steht im Tooltip. `opt.tooltip` gilt je Option.
  tooltip,
  // 🔴 G0.4: `'field'` = die heutige Form (Vorgabe). `'pill'` = die
  // Facetten-Pille; in G0 von NIEMANDEM aufgerufen — sie ist die Vorbereitung
  // fuer die Ansichtskarten G1 ff.
  desktopForm = 'field',
  // 🔴 R16-W2 (Wiedervorlage 2, Regel 22 §5): der Baustein kann nicht wissen,
  // WARUM die Optionsliste leer ist. Er nimmt den Grund als Prop, statt „es
  // gibt nichts" zu raten — unter `error`/`unknown` waere das die falsche
  // Aussage („unbekannt ist nie 0", hier: „unbekannt ist nie leer").
  // Nur die PILLE liest es; die Vorgabe `'ready'` aendert nichts.
  // 🔴 R16-C1 (Wiedervorlage 3): VIER Werte — `'ready' | 'loading' | 'error' |
  // 'unknown'`. Der vierte hat einen eigenen Satz („Options not loaded yet"),
  // und ein Wert ausserhalb der Menge WIRFT (Tabelle und Wurf in `leerText.js`).
  optionsStatus = 'ready',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  // 🔴 R16 N3 (Wiedervorlage 1): MUI 7 setzt am Papier eines temporaeren
  // Drawers bereits `role="dialog"` + `aria-modal="true"` (`Drawer.js:286`) —
  // `aria-haspopup="dialog"` am Ausloeser stimmt also. Es fehlte nur der NAME:
  // das Blatt ist ein Dialog ohne Beschriftung. Die Titelzeile traegt jetzt
  // eine `id`, und das Papier verweist per `aria-labelledby` darauf.
  const titelId = useId();
  // 🔴 Der Name des Desktop-Feldes. `InputLabel` und `Select` sind in MUI NICHT
  // von selbst verbunden — ohne `labelId` bleibt das Etikett ohne Bezug und
  // `getByLabelText('Category')` findet nur den Text, kein Bedienelement.
  //
  // 🔴 Nur `labelId`, KEIN `id` am `Select`: MUI setzt dann
  // `aria-labelledby="<labelId> <id>"` (`SelectInput.js:461`), und der
  // zugängliche Name zöge den angezeigten Wert mit ein („Category Coffee").
  // Nur `labelId` ⇒ der Name ist genau die Beschriftung.
  const selectLabelId = useId();
  // Der Anker des Popovers der Facetten-Pille (`desktopForm="pill"`).
  const anker = useRef(null);
  // Die Liste, auf die der Ausloeser per `aria-controls` zeigt (R16-W7).
  const listenId = useId();
  // 🔴 R16-W1 (Wiedervorlage 3): die Leerzeile traegt eine Kennung, damit die
  // `listbox` sie per `aria-describedby` als BESCHREIBUNG fuehren kann. Ohne
  // sie stuende der Grund nur gedruckt da: die leere Liste ist nicht
  // fokussierbar (`autoFocusItem` findet keine Zeile), ein Vorleser kaeme an
  // den Satz nie heran.
  // 🔴 ABGELEITET, kein zweiter `useId()`-Aufruf: ein zusaetzlicher `useId`
  // verschiebt die Zaehlung und damit JEDE danach erzeugte Kennung im Baum.
  // Gemessen an der A/B-Renderprobe der fuenf Bestandsflaechen: gleiche Laenge,
  // andere Pruefsumme, Unterschied genau `id=":r7:"` gegen `id=":r8:"`. Die
  // Zeile haengt deshalb an der Kennung der Liste, die sie beschreibt.
  const leerId = `${listenId}-leer`;

  // 🔴 NICHT-MODAL heisst: Aussenklick und Esc schliessen, sonst nichts
  // (R16-W5). Ein MUI-`Popover` sitzt auf `Modal` und braechte drei Dinge mit,
  // die das ausschliesst: eine Fokusfalle (`TrapFocus`), `aria-hidden` auf
  // allen Geschwistern und einen unsichtbaren Hintergrund, der den
  // Aussenklick SCHLUCKT — das angeklickte Element erreicht ihn nie. Deshalb
  // `Popper` (kein Modal).
  //
  // 🔴 Der Aussenklick sitzt seit der Wiedervorlage 2 (R15-Bedingung 3) am
  // `ClickAwayListener` um das `Paper` — Hausform G0.5 (`FilterSheet.jsx`),
  // Vorbild `rma/components/ShipmentStatusPopover.jsx:92-96`. Der eigene
  // `mousedown`-Zuhoerer ist entfallen: er war eine DRITTE Bauart derselben
  // Eigenschaft in zwei Bausteinen derselben Welle (G0.4 hier, G0.5 dort) und
  // hing an einem Ereignis, das die Hausform nicht benutzt.
  //
  // 🔴 Hier bleibt nur `Esc`. Es gibt keinen `Popper`-Wurzelknoten, der eine
  // Taste faengt (er traegt `role="presentation"`, s. u.), die Flaeche ist
  // NICHT modal — ein `Esc` erreicht sie also nie von selbst. Dokumentweit ist
  // die einzige Stelle, an der es ankommt; dieselbe Form wie in G0.5.
  //
  // 🔴 Der Effekt steht HIER und nicht im Pille-Zweig: der Zweig ist ein
  // vorzeitiger `return`, ein `useEffect` darin verletzte die Rules of Hooks.
  // Fuer jeden Bestandsaufrufer tut er NICHTS — die Bedingung unten ist fuer
  // sie immer falsch (`desktopForm` ist dort `'field'`).
  useEffect(() => {
    if (!open || isMobile || desktopForm !== 'pill') return undefined;
    const aufEscape = (e) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      anker.current?.focus();
    };
    document.addEventListener('keydown', aufEscape);
    return () => document.removeEventListener('keydown', aufEscape);
  }, [open, isMobile, desktopForm]);

  // 🔴 R16-C1 (Wiedervorlage 3): der Grund ist eine GESCHLOSSENE Menge. Ein
  // unbekannter Wert WIRFT — und zwar HIER, fuer jeden Zweig, nicht erst beim
  // Lesen der leeren Liste: sonst bliebe ein Tippfehler in `optionsStatus` an
  // einer gefuellten Liste unsichtbar. Hausform `EmptyState.jsx:122-124`.
  // Der vierte Grund (`unknown`) hat einen EIGENEN Satz; die erste Fassung
  // bildete ihn still auf `ready` = „No options" ab — genau die Behauptung, die
  // Konzept §8.1 / N8.3 fuer diese Lage ausschliesst („unbekannt ist nie leer").
  const leerSatz = leerTextFuer(optionsStatus);

  // ── Facetten-Pille (desktopForm="pill") ───────────────────────
  // 🔴 Plan §G0.4 F.1–F.5. Der Zweig feuert NUR auf dem DESKTOP und NUR, wenn
  // ein Aufrufer `desktopForm="pill"` uebergibt — in G0 tut das niemand, er ist
  // die Vorbereitung fuer die Ansichtskarten G1 ff.
  // 🔴 Auf dem TELEFON bleibt der Ausloeser unveraendert (Konzept §12 BS7:
  // „mobil unverändert Blatt"). Seine `aria-haspopup`-Zusage bleibt dort
  // `"dialog"` — die Flaeche IST ein Blatt mit `role="dialog"`. Die erste
  // Fassung sagte dort `"listbox"` an und versprach damit eine Liste, die nicht
  // kommt (R16-W1 / R15-W4, 2026-10-12; Konzept §12 BS7 :878 ist mitgezogen).
  if (!isMobile && desktopForm === 'pill') {
    const selected = options.find((o) => o.value === value);
    const hasValue = value !== undefined && value !== null && value !== '';
    // 🔴 Der Ausloeser SCHALTET UM (Hausform MUI-`Select`, Vorbild G0.5
    // `FilterSheet.jsx`): ein Klick auf den OFFENEN Ausloeser schliesst. Bis zur
    // Wiedervorlage 2 war das ein Leerlauf — `aria-expanded` blieb `true`
    // (R15-Bedingung 3).
    const umschalten = () => setOpen((v) => !v);
    const schliesse = () => setOpen(false);
    const pilleText = hasValue ? `${label}: ${selected?.label ?? value}` : label;
    // 🔴 R16-W4/R16-W2: eine Facette ohne Optionen ist kein leerer Kasten. Die
    // Liste nennt den GRUND (Konzept §11, Zeile „Abhängigkeiten": „zeigt den
    // Grund, wenn sie leer ist; nie still deaktiviert"; Regel 22 §5).
    // 🔴 Warum hier TEXT in der Flaeche und kein `DisabledReason`-Tooltip,
    // obwohl die Konzeptzeile das Muster nennt: `DisabledReason`s eigener
    // `@dontUseFor` schliesst „a permanent disabled state that no user action
    // can clear" aus — und genau das ist eine leer gelieferte Facette. Eine
    // deaktivierte Pille koennte ihr Popover zudem gar nicht mehr oeffnen; der
    // Grund waere unerreichbar. Der sichtbare Satz ist die Form, die beide
    // Einwände erfuellt.
    // 🔴 Warum die Zeile AUSSERHALB der `listbox` steht: `role="presentation"`
    // als Kind einer `role="listbox"` verletzt ARIA 1.2 (die Liste fuehrt
    // `option`/`group` als einziges erlaubtes Kind) — R16-W1. Eine
    // `role="option"`-Zeile waere die andere Aufloesung, sie stuende aber als
    // waehlbare Option in der Liste, die keine ist.
    const leerText = options.length === 0 ? leerSatz : null;
    // 🔴 R16-W1 (Wiedervorlage 3): der Grund steht auch MASCHINELL im DOM —
    // `data-reason` mit den Woertern der Hausform `EmptyState` (`ready` heisst
    // dort `no-data`; die Lade-Lage traegt GAR KEIN `data-reason`). Pruefflaeche
    // P9/P33 (`[data-reason]`); ohne die Zeile ist der Grund unsichtbar.
    const leerGrund = options.length === 0 ? grundImDom(optionsStatus) : undefined;
    // 🔴 R16-Blocker 1: die Option, die den Fokus beim Oeffnen bekommt — die
    // GEWAEHLTE, sonst (Index −1) die erste ueber `autoFocusItem` der Liste.
    const gewaehltIndex = options.findIndex((o) => o.value === value);

    // 🔴 R16-Blocker 1 / R15-Bedingung 3: Aussenklick schliesst, der Ausloeser
    // ist ausgenommen (sonst schloesse und oeffnete derselbe Klick). Hausform
    // `rma/components/ShipmentStatusPopover.jsx:92-96`, Bauform wie G0.5.
    const beiAussenklick = (e) => {
      if (anker.current?.contains(e.target)) return;
      schliesse();
    };

    const pille = (
      <Box
        ref={anker}
        // 🔴 `alsKnopf` traegt `role`, `tabIndex` UND `aria-haspopup`: bis zur
        // Wiedervorlage 2 hing `aria-haspopup` an einer eigenen Bedingung und
        // sagte bei `disabled` eine Flaeche an, deren Rolle fehlte (R15-W2 /
        // R16-W4).
        // 🔴 R16-W3 (Wiedervorlage 3, 2026-10-12): `role` bleibt bei `disabled`
        // STEHEN — MUI-Konvention fuer ein nicht-natives Bedienelement
        // (`ButtonBase` behaelt die Rolle und setzt `aria-disabled` +
        // `tabIndex={-1}`). Ohne die Rolle haette `aria-disabled` keinen
        // Traeger: ARIA 1.2 §6.5 kuendigt die globale Verwendung ab, getragen
        // wird es von `button` (Used in Roles). Ein rollenloser Knoten ist fuer
        // Hilfsmittel nur Text. `tabIndex={-1}` haelt ihn weiterhin aus der
        // Tab-Reihenfolge.
        role="button"
        tabIndex={disabled ? -1 : 0}
        // 🔴 Die Pille oeffnet eine `listbox` und sagt genau das an. Auf dem
        // TELEFON traegt derselbe Ausloeser `"dialog"` (Blattform) — die beiden
        // Breiten sagen also nicht dasselbe an, sondern jede die Wahrheit ueber
        // ihre eigene Flaeche (R16-W1 / R15-W4, 2026-10-12).
        // 🔴 Und BEIDE Zusagen haengen am `disabled`-Waechter (R16-W3): eine
        // deaktivierte Pille oeffnet nichts, sie darf keine Flaeche ansagen —
        // eine Zusage ohne die Flaeche, die sie verspricht, ist derselbe Fehler
        // wie eine Zusage ohne Rolle.
        aria-haspopup={disabled ? undefined : 'listbox'}
        aria-expanded={disabled ? undefined : open}
        // 🔴 R16-W7: Ausloeser und Liste sind zusaetzlich per ID verbunden,
        // nicht nur ueber `aria-haspopup`/`aria-expanded`.
        aria-controls={open ? listenId : undefined}
        aria-disabled={disabled || undefined}
        onClick={disabled ? undefined : umschalten}
        onKeyDown={disabled ? undefined : (e) => {
          // 🔴 Nur der AUSLOESER selbst oeffnet. Ohne die Zeile steigt das
          // Enter des ✕ (eines echten Knopfes in ihm) bis hierher und oeffnete
          // zusaetzlich das Popover.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            umschalten();
          }
        }}
        sx={{
          ...FACET_PILL_SX,
          gap: SP.chip,
          // Rechts weniger Rand, sobald das ✕ darin sitzt — sonst steht es
          // 16 px vom Pillenrand entfernt und liest sich als eigenes Element.
          pr: clearable && hasValue && !disabled ? SP.chip : undefined,
          color: hasValue ? 'primary.main' : 'text.primary',
          borderColor: hasValue ? 'primary.main' : 'var(--glass-border)',
          fontWeight: hasValue ? 600 : 500,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'default' : 'pointer',
          ...sx,
        }}
      >
        <Typography
          sx={{
            fontSize: FS.sm,
            fontWeight: 'inherit',
            lineHeight: 1,
            // 🔴 R16-W3: ohne Kappung wuchs die Pille unbegrenzt. Die Suche
            // fuellt Z1 (N2.9), die Facette steht rechts daneben — ein
            // beliebig langer Wert verdraengte sie. Der FELD-Zweig desselben
            // Bausteins kuerzt laengst (`textOverflow: 'ellipsis'`).
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: FACET_PILL_MAX_WIDTH_PX,
          }}
        >
          {pilleText}
        </Typography>
        <KeyboardArrowDownIcon sx={{ fontSize: FS.sm, flexShrink: 0 }} />
        {/* 🔴 R16-W3 (Wiedervorlage 2): `disabled` schliesst das ✕ AUS. Bis
            dahin hing der Knopf nur an `clearable && hasValue` — ein Element,
            das sich `aria-disabled` nennt, trug einen bedienbaren Knopf, der
            `onChange` ruft. Der Kasten selbst war korrekt abgeschaltet, das ✕
            daneben nicht. */}
        {clearable && hasValue && !disabled && (
          // 🔴 Hausform fuer ein Bedienelement in einem Bedienelement
          // (`pattern-library.md` §„Jede Flaeche, die sich als Knopf ausgibt,
          // ist ein Knopf", Vorbild `SearchTrigger`): echtes `IconButton` mit
          // `aria-label`, `stopPropagation` im `onClick`, damit der umgebende
          // Kasten nicht zusaetzlich oeffnet.
          <IconButton
            size="small"
            aria-label={`Clear ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              // 🔴 Blocker 2 (R16): der Knopf haengt an `clearable && hasValue`.
              // Mit dem Leeren wird `hasValue` falsch, das ✕ VERSCHWINDET, und
              // der Browser setzt den Fokus auf `document.body` — der naechste
              // `Tab` beginnt am Dokumentanfang statt an der Pille (WCAG 2.4.3).
              // `focus()` laeuft SYNCHRON im Handler, also BEVOR React neu
              // rendert; danach waere das Ziel nicht mehr erreichbar
              // (Hausform `SearchTrigger.jsx:265-289`).
              anker.current?.focus();
              onChange('');
            }}
            // 🔴 R16-W2: mit `p: SP.chip` allein maß das ✕ rund 20,5 px und lag
            // damit unter der 24-px-Grenze (WCAG 2.5.8). Der Theme-Boden greift
            // hier NICHT — er gilt nur unter `sm` oder auf GROBEM Zeiger
            // (`theme.js:1069/1080`); ein 1440-px-Desktop mit feinem Zeiger
            // blieb darunter. `TOUCH_TARGET_MIN` ist der Token fuer genau diese
            // Untergrenze.
            sx={{ ...TOUCH_TARGET_MIN, p: SP.chip, ml: SP.chip, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: FS.sm }} />
          </IconButton>
        )}
      </Box>
    );

    return (
      <>
        {tooltip ? <Tooltip title={tooltip} describeChild>{pille}</Tooltip> : pille}

        {/* 🔴 `Popper`, NICHT `Popover` — MUI's `Popover` sitzt auf `Modal`:
            Fokusfalle, `aria-hidden` auf den Geschwistern und ein Hintergrund,
            der den Aussenklick schluckt. Begruendung im Effekt oben.
            🔴 `role="presentation"` (R16-W5, in jsdom GEMESSEN): MUI's `Popper`
            traegt am Wurzelknoten die Vorgabe `role="tooltip"`. Ohne diese Zeile
            saesse eine interaktive `listbox` in einem `tooltip` — eine fremde
            Rolle zwischen Ausloeser und Liste. *Widerlegt haette:* ein
            Wurzelknoten ohne `role` — dann stuende hier nichts. Gemessen:
            `document.querySelector('[role="tooltip"]')` findet ihn.
            🔴 KEIN `disablePortal`: die Leiste traegt die Ueberlaufmaske aus
            G0.1 (`overflow: hidden`), eine ungeportete Flaeche wuerde daran
            abgeschnitten. Der Fokus kommt ueber `autoFocusItem` hinein, nicht
            ueber die Naehe im DOM. */}
        <Popper
          open={open}
          anchorEl={anker.current}
          placement="bottom-start"
          role="presentation"
          sx={{ zIndex: (t) => t.zIndex.modal }}
        >
          {/* 🔴 Der `ClickAwayListener` liegt UM das `Paper` (Bauform G0.5
              `FilterSheet.jsx:556`): ein Klick INNERHALB zaehlt nicht als
              „aussen", ein Klick daneben schon. Der Ausloeser ist in
              `beiAussenklick` ausgenommen. */}
          <ClickAwayListener onClickAway={beiAussenklick}>
            <Paper
              sx={{ mt: SP.chip, minWidth: 180, maxHeight: LISTBOX_MAX_HEIGHT_PX, overflowY: 'auto' }}
            >
              {/* 🔴 `MenuList`, nicht `List`: nur `MenuList` bringt die
                  Roving-Navigation, die `role="listbox"` laut ARIA-APG zusagt
                  (Pfeiltasten, `Home`/`End`, Typeahead) — und `autoFocusItem`
                  holt den Fokus beim Oeffnen in die Flaeche. MUI's `List` ist
                  ein gestyltes `<ul>` und fuehrt NICHTS davon; die Runde-1-Zeile
                  „MUI `List` fuehrt die Roving-Tabindex-Navigation selbst" hat
                  `List` mit `MenuList` verwechselt (R16-Blocker 1). */}
              <MenuList
                id={listenId}
                role="listbox"
                aria-label={label}
                // 🔴 R16-W1 (Wiedervorlage 3): die leere Liste ist nicht
                // fokussierbar (`autoFocusItem` findet keine Zeile), ihr Grund
                // waere also stumm. Er haengt als BESCHREIBUNG an der Liste.
                aria-describedby={leerText ? leerId : undefined}
                dense
                disablePadding
                autoFocusItem
                onKeyDown={(e) => {
                  // 🔴 MUI-`Menu`-Verhalten (R16-Blocker 1 / R15-Bedingung 4):
                  // `Tab` schliesst die Flaeche und gibt den Fokus an den
                  // Ausloeser zurueck — die Liste ist kein eigenes Sprungziel im
                  // Dokumentfluss. `preventDefault` haelt den `Tab` fest, das
                  // `focus()` setzt ihn dorthin, wo er hingehoert.
                  if (e.key !== 'Tab') return;
                  e.preventDefault();
                  schliesse();
                  anker.current?.focus();
                }}
              >
                {/* 🔴 KEINE „All"-Option (Konzept §11 N3.2 / P25: „Eine Option
                    ‚All'/'Any' gibt es in Facetten NICHT"). Geraeumt wird mit
                    dem ✕ in der Pille (dieselbe Konzeptzeile: „Status: Open,
                    Waiting ⌄ ✕") — `clearable` steuert HIER also nur noch das
                    ✕. Die „All"-Zeile des FELD-Zweigs weiter unten ist davon
                    NICHT beruehrt; sie beschreibt die Vorgabe `field`. */}
                {options.map((opt, i) => {
                  const active = value === opt.value;
                  return (
                    <MenuItem
                      key={opt.value}
                      role="option"
                      aria-selected={active}
                      // 🔴 R16-Blocker 1: der Fokus geht auf die GEWAEHLTE
                      // Option, sonst auf die erste. Ohne gewaehlten Wert ist
                      // `gewaehltIndex` −1, kein Eintrag traegt `autoFocus`, und
                      // `autoFocusItem` an der Liste uebernimmt die erste Zeile.
                      autoFocus={i === gewaehltIndex}
                      // 🔴 R16-Blocker 1 (Wiedervorlage 3, 2026-10-12): die
                      // Auswahl SCHLIESST die Flaeche, der `Popper` baut seinen
                      // Inhalt ab (Probe F.18 sichert genau das zu) — die
                      // fokussierte `role="option"` verschwindet, und der
                      // Browser setzt den Fokus auf `document.body`; der
                      // naechste `Tab` begaenne am Dokumentanfang (WCAG 2.4.3).
                      // Dieselbe Form wie beim ✕ wenige Zeilen weiter oben:
                      // `focus()` laeuft SYNCHRON im Handler, also BEVOR React
                      // neu zeichnet. 🔴 Der Weg traegt Klick UND Tastatur:
                      // `MenuItem` ist kein natives `<button>`, `ButtonBase`
                      // loest `onClick` fuer Enter (keydown) und Leertaste
                      // (keyup) aus.
                      onClick={() => { anker.current?.focus(); onChange(opt.value); schliesse(); }}
                    >
                      <ListItemText
                        primary={optionText(opt)}
                        primaryTypographyProps={{
                          variant: 'body2',
                          color: active ? 'primary.main' : 'text.primary',
                          fontWeight: active ? 700 : 400,
                        }}
                      />
                      {active && <CheckIcon fontSize="small" color="primary" />}
                    </MenuItem>
                  );
                })}
              </MenuList>
              {/* 🔴 Der Leerzustand steht AUSSERHALB der `listbox` (R16-W1):
                  ARIA 1.2 laesst als Kind einer `listbox` nur `option`/`group`
                  zu. Als `role="presentation"`-Zeile IN der Liste verletzte er
                  das; als `option` stuende er als waehlbare Zeile da, die keine
                  ist. */}
              {leerText && (
                // 🔴 `id` (fuer `aria-describedby` der Liste) und `data-reason`
                // (die maschinelle Pruefflaeche, R16-W1) — beide in den Woertern
                // der Hausform `EmptyState`.
                <Box id={leerId} data-reason={leerGrund} sx={{ px: 2, py: 1 }}>
                  <Typography variant="body2" color="text.secondary">{leerText}</Typography>
                </Box>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  // ── Desktop: standard MUI Select ──────────────────────────────
  if (!isMobile) {
    return (
      <FormControl size="small" disabled={disabled} sx={{ minWidth: 160, ...sx }}>
        {/* 🔴 E1b Folgerunde 12 (2026-10-08): dasselbe Etikett wie
            `BottomSheetMultiSelect` — es SCHWEBT immer (`shrink` + `notched`,
            Hausform `VendorDetails.jsx:1072-1095`). Entscheid und Begruendung
            aus `362ac27f43` („Vendor-Etikett schwebt"): ein leeres Etikett im
            Feld steht dort, wo sonst der Wert steht, und liest sich als Wert.
            Dort galt die Regel nur fuer die MEHRFACHAUSWAHL; ein Einfach-Select
            daneben hat immer einen Wert und schrumpft von selbst. Genau das
            trifft aber nicht jeden Aufrufer: `Case type` auf `/rma` und `Type`,
            `Channel`, `Tax case` auf `/invoicing` stehen LEER (ihr Wert kommt aus
            der URL bzw. ist `''`) — dort standen zwei Formen in einer Reihe.
            `displayEmpty` haelt den leeren Anzeigebereich (MUI zeigt sonst
            nichts und der Kasten faellt zusammen); `renderValue` liefert fuer den
            leeren Wert bewusst NICHTS — das Etikett schwebt darueber und ist die
            einzige Aussage, genau wie der Ausloeser auf dem Telefon nur das
            Etikett zeigt. Ein Wert, den `options` nicht kennt, zeigt weiterhin
            nichts (wie vorher), damit eine veraltete URL nicht als Wert erscheint.
            🔴 `shrink` allein genuegt NICHT: MUI leitet das Schrumpfen aus
            `value !== ''` ab (`UnifiedCreateWizard.jsx:2299`, E-W6) — und die
            Kerbe oeffnet `notched`, sonst laeuft die schwebende Beschriftung
            durch die Rahmenlinie. */}
        {/* 🔴 Welle 3 PR 3 (R16 W1): der Rückverweis sass als `title` am
            `<label>` — nicht fokussierbar, also per Tastatur unerreichbar. Er
            hängt jetzt am fokussierbaren `Select` (`describeChild`: der sichtbare
            Name bleibt `label`, der deutsche Begriff wird zur Beschreibung). */}
        <InputLabel id={selectLabelId} shrink>{label}</InputLabel>
        {(() => {
          const auswahl = (
            <Select
              labelId={selectLabelId}
              label={label}
              displayEmpty
              // 🔴 bewusst REDUNDANT, gemessen 2026-10-08 (R15 K3): MUI leitet
              // `notched` aus `displayEmpty` ab (`Select.js`, `notched: true`).
              // Nimmt man NUR diese Zeile weg, bleibt die Kerben-Probe gruen;
              // erst ohne `displayEmpty` wird sie rot. Sie steht trotzdem hier,
              // damit die Absicht im Aufruf lesbar bleibt — die Probe
              // (`BottomSheetSelect.etikett.test.jsx`) sichert den ENDZUSTAND
              // (der Rahmen ist gekerbt), nicht diese einzelne Angabe.
              notched
              value={value ?? ''}
              onChange={(e) => onChange(e.target.value)}
              renderValue={(gewaehlt) =>
                // Leerer Wert → leere Anzeige: das schwebende Etikett ist die
                // einzige Aussage, genau wie der Ausloeser auf dem Telefon nur
                // das Etikett zeigt.
                // 🔴 Der PLATZHALTER der Hausform wird BEWUSST nicht uebernommen
                // (R16 W-N1, 2026-10-08): `UnifiedCreateWizard` („Select
                // category") und `VendorDetails` („No preference") setzen im
                // leeren Fall einen Text, hier bleibt der Kasten leer. Grund:
                // die Geschwister-Selects derselben Filterzeile zeigen im leeren
                // Fall ebenfalls nichts, und leer heisst hier „kein Filter" —
                // benannt wird er vom schwebenden Etikett. Die Zitierung der
                // Hausform oben meint die ETIKETTLAGE, nicht den Platzhalter.
                // Ein Wert, den `options` nicht kennt, zeigt
                // ebenfalls nichts — wie vorher —, damit eine veraltete Adresse
                // nicht als Wert erscheint. Fuer alle uebrigen Werte ist das die
                // Beschriftung aus `options`, also zeichengleich mit dem, was
                // MUI vorher aus dem passenden `MenuItem` gelesen hat.
                gewaehlt ? (options.find((o) => o.value === gewaehlt)?.label ?? '') : ''
              }
            >
              {clearable && <MenuItem value=""><em>All</em></MenuItem>}
              {/* 🔴 KEIN `Tooltip` um die `MenuItem`: MUI `Select` sucht das
                  gewaehlte Kind unter seinen DIREKTEN Kindern (`props.value`).
                  Ein Wrapper dazwischen hat kein `value` — die Zeile waere dann
                  nicht mehr auswaehlbar. Der Rückverweis bleibt darum als
                  natives `title` am fokussierbaren Eintrag; der benannte
                  R16-W1-Sitz ist der `<InputLabel>`, und der haengt jetzt am
                  `Select`.
                  ⚠️ Den ANZEIGETEXT liest MUI seit dem 2026-10-08 nicht mehr aus
                  `props.children`, sondern aus `renderValue` oben — die
                  `MenuItem`-Kinder tragen ihn nur noch fuer die Liste im
                  aufgeklappten Menue. */}
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} title={opt.tooltip}>
                  {optionText(opt)}
                </MenuItem>
              ))}
            </Select>
          )
          return tooltip ? <Tooltip title={tooltip} describeChild>{auswahl}</Tooltip> : auswahl
        })()}
      </FormControl>
    );
  }

  // ── Mobile: glass pill ─────────────────────────────────────────
  const selected = options.find((o) => o.value === value);
  const hasValue = value !== undefined && value !== null && value !== '';
  const triggerLabel = selected?.label ?? label;

  const oeffne = () => setOpen(true)

  const ausloeser = (
    <Box
      onClick={disabled ? undefined : oeffne}
      onKeyDown={disabled ? undefined : (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          oeffne()
        }
      }}
      // 🔴 R16-W3 (Wiedervorlage 3, 2026-10-12): dieselbe Form wie in der Pille.
      // `role` bleibt bei `disabled` STEHEN und traegt dann `aria-disabled` —
      // ohne die Rolle haette die Absage keinen Traeger: ARIA 1.2 §6.5
      // kuendigt die globale Verwendung ab, getragen wird es von `button`
      // (Used in Roles). Die ZUSAGEN (`aria-haspopup`, `aria-expanded`) fallen
      // mit `disabled` weg: ein abgeschalteter Ausloeser oeffnet nichts. Kein
      // Verbraucher uebergibt `disabled` an `BottomSheetSelect` (gemessen,
      // 2026-10-12) — die Ausgabe bleibt Zeichen fuer Zeichen dieselbe.
      role="button"
      tabIndex={disabled ? -1 : 0}
      // 🔴 R16-W1 / R15-W4 (2026-10-12): HIER steht `"dialog"` — die
      // Flaeche ist das Blatt, und das traegt `role="dialog"` (eigene Probe:
      // `BottomSheetSelect.a11y.test.jsx`). Die erste Fassung sagte in der
      // Facettenform `"listbox"` an und versprach damit eine Liste, die auf dem
      // Telefon nie kommt. Fuer jeden Bestandsaufrufer ist die Zeichenkette
      // ohnehin Zeichen fuer Zeichen unveraendert.
      aria-haspopup={disabled ? undefined : 'dialog'}
      aria-expanded={disabled ? undefined : open}
      aria-disabled={disabled || undefined}
      sx={{
        ...GLASS_PILL_SX,
        gap: 0.5,
        color: hasValue ? 'primary.main' : 'text.primary',
        borderColor: hasValue ? 'primary.main' : 'var(--glass-border)',
        fontWeight: hasValue ? 600 : 500,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
        ...sx,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 'inherit', lineHeight: 1, whiteSpace: 'nowrap' }}>
        {triggerLabel}
      </Typography>
      <KeyboardArrowDownIcon sx={{ fontSize: 14, flexShrink: 0 }} />
    </Box>
  )

  return (
    <>
      {/* 🔴 Welle 3 PR 3 (R16 W1): der Auslöser war eine klickbare Box OHNE
          Fokus — der Rückverweis als `title` war damit per Tastatur UND per
          Touch unerreichbar. Er ist jetzt ein fokussierbarer Knopf
          (`role`/`tabIndex`/Enter+Leertaste), und der Tooltip hängt per
          `describeChild` an ihm. Ohne `tooltip` bleibt er unverpackt — ein
          leeres Tooltip setzte ein `aria-describedby` auf nichts. */}
      {tooltip ? <Tooltip title={tooltip} describeChild>{ausloeser}</Tooltip> : ausloeser}

      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        disableSwipeToOpen
        {...BOTTOM_SHEET_TRANSITION}
        PaperProps={{ 'aria-labelledby': titelId, sx: { borderRadius: '16px 16px 0 0', maxHeight: '80vh' } }}
      >
        <Box sx={{ pt: 2, pb: 4 }}>
          {/* Handle bar */}
          <Box sx={{ width: 36, height: 4, borderRadius: 99, bgcolor: 'divider', mx: 'auto', mb: 2 }} />

          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2.5, mb: 1 }}>
            <Typography id={titelId} variant="subtitle2" fontWeight={700}>
              {tooltip ? <Fachbegriff label={label} begriff={tooltip} /> : label}
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <List disablePadding>
            {clearable && (
              <ListItemButton
                onClick={() => { onChange(''); setOpen(false); }}
                sx={{ mx: 1, borderRadius: 1 }}
              >
                <ListItemText
                  primary="All"
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: !hasValue ? 'primary.main' : 'text.primary',
                    fontWeight: !hasValue ? 700 : 400,
                  }}
                />
                {!hasValue && <CheckIcon fontSize="small" color="primary" />}
              </ListItemButton>
            )}
            {options.map((opt) => {
              const active = value === opt.value;
              const zeile = (
                <ListItemButton
                  key={opt.value}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  sx={{ mx: 1, borderRadius: 1 }}
                >
                  <ListItemText
                    primary={optionText(opt)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      color: active ? 'primary.main' : 'text.primary',
                      fontWeight: active ? 700 : 400,
                    }}
                  />
                  {active && <CheckIcon fontSize="small" color="primary" />}
                </ListItemButton>
              );
              // Der Rückverweis hängt am fokussierbaren Eintrag, nicht mehr als
              // `title` an der Typografie darin (Welle 3 PR 3, R16 W1).
              return opt.tooltip
                ? <Tooltip key={opt.value} title={opt.tooltip} describeChild>{zeile}</Tooltip>
                : zeile;
            })}
          </List>
        </Box>
      </SwipeableDrawer>
    </>
  );
}
